import React, { Fragment, useState, useContext, useEffect, useCallback } from "react";
import {
  Col,
  Button,
  Row,
  Input,
  ButtonGroup,
  Alert as Alert2
} from "reactstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { api, constants, UserContext, filterHelpers, helpers } from "../utils";
import { FilterSelect, FilterSwitch } from "../components";
import { DesktopContainer } from "../layouts";
import makeAnimated from "react-select/animated";
import { MdAddCircleOutline, MdDeleteForever } from "react-icons/md";

const mPlusIcon = (<MdAddCircleOutline className="" />);
const mTrash = (<MdDeleteForever className="" />);

const SELECT_STYLE_SETTINGS = {
    control: (provided, state) => ({
      ...provided,
      boxShadow: "none",
      border: "none"
    }),
    // react-select v5 defaults valueContainer to `flexWrap: "wrap"`, so long
    // option labels wrap to a second line and make individual matrix cells taller
    // than their neighbors, breaking the grid alignment. Keep single-line (v4
    // behavior) so every cell's select is the same height; the singleValue's
    // default ellipsis handles overflow.
    valueContainer: (provided, state) => ({
      ...provided,
      flexWrap: "nowrap",
    }),
    container: (provided, state) => ({
      ...provided,
      boxShadow: "none",
      border: "none"
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      display: "none"
    }),
    indicatorSeparator: (provided, state) => ({
      ...provided,
      display: "none"
    }),
  };

const WORKOUT_SELECT_STYLE_SETTINGS = Object.assign({}, SELECT_STYLE_SETTINGS, {
    input: (provided, state) => ({
        ...provided,
        color: "blue"
      }),
    singleValue: (provided, state) => ({
        ...provided,
        color: "blue"
      })
});

// Build the animated react-select components ONCE. Calling makeAnimated() inline
// in render creates a new components object every render, which defeats
// react-select's memoization and forces every <Select> on the screen to fully
// re-render. A single shared instance is safe because it depends on nothing.
const animatedComponents = makeAnimated();

// Memoized so that editing one category/movement (or any unrelated state change
// in the parent — loading flags, messages, filters) does NOT re-render every
// other category's block and its react-selects. This relies on the parent's
// state updates being immutable (new refs only for what changed) and on the
// callback props being stable (useCallback), both handled below.
const ProgramSectionAndMatrix = React.memo(function ProgramSectionAndMatrix({
    category,
    mtrxList,
    mvmtList, 
    exGroups, 
    changeCategoryMovementCallback, 
    changeMatrixExerciseCallback, 
    changeHeaderCallback, 
    addCategoryCallback,
    removeCategoryCallback,
    addMovementCallback,
    removeMovementCallback,
    lastCategory}){
    return (<div key={`${category?.id}`}>
        {/* Category header: title input + add/remove-category buttons share the first (wide) column */}
        <div className="flex mx-1">
            <div className="program-cell blue-bg-white-txt workout-header-left-border-blue workout-header-right-border-blue w-72 shrink-0 flex items-center gap-1">
                <div className="flex-1 min-w-0">
                    <Input
                        value={category.category.header || ''}
                        onChange={(e) => {
                            changeHeaderCallback(category.category.seq, "header", e.target.value);
                        }}
                    />
                </div>
                <ButtonGroup>
                    <Button
                        className="program-header-add"
                        style={{border: "white"}}
                        size="sm"
                        onClick={() => addCategoryCallback(category.category.seq)}
                    >
                        {mPlusIcon}
                    </Button>
                    <Button
                        color="danger"
                        size="sm"
                        onClick={() => removeCategoryCallback(category.category.seq)}
                    >
                        {mTrash}
                    </Button>
                </ButtonGroup>
            </div>
            {_.times(7, (i) => (
                <div key={`header-${i + 1}`} className="program-cell-header matrix-cell-l-margin blue-bg-white-txt workout-header-x-border flex-1 min-w-0">{category.category.header}</div>
            ))}
        </div>
        {/* Sets subheader row */}
        <div className="flex mx-1">
            <div className="program-cell blue-bg-white-txt workout-header-left-border-blue workout-header-right-border-blue w-72 shrink-0">
                <Input
                    value={category.category.setsSubheader || ''}
                    onChange={(e) => {
                        changeHeaderCallback(category.category.seq, "setsSubheader", e.target.value);
                    }}
                />
            </div>
            {_.times(7, (i) => (
                <div key={`sets-${i + 1}`} className="program-cell matrix-cell-l-margin blue-bg-white-txt workout-header-x-border flex-1 min-w-0">{category.category.setsSubheader}</div>
            ))}
        </div>
        {/* Reps subheader row */}
        <div className="flex mx-1">
            <div className="program-cell blue-bg-white-txt workout-header-left-border-blue workout-header-right-border-blue w-72 shrink-0">
                <Input
                    value={category.category.repsSubheader || ''}
                    onChange={(e) => {
                        changeHeaderCallback(category.category.seq, "repsSubheader", e.target.value);
                    }}
                />
            </div>
            {_.times(7, (i) => (
                <div key={`reps-${i + 1}`} className="program-cell matrix-cell-l-margin blue-bg-white-txt workout-header-x-border flex-1 min-w-0">{category.category.repsSubheader}</div>
            ))}
        </div>
        {_.map(category.movements, (m, mIdx) => {
            let matrix = _.find(mtrxList, mx => mx.movementId === m.movementId);
            let lastMovement = mIdx === category.movements.length - 1;
            if (!matrix) {
              matrix = mtrxList.length
                ? mtrxList[0]
                : null;
            }
            const bottomBorder = lastCategory && lastMovement ? "workout-header-bottom-border-blue" : "";
            return (
                <div key={`${m.seq}-${m.movementId}`} className="flex mx-1">
                    {/* First column: movement select + add/remove-movement buttons together */}
                    <div className={`program-cell workout-header-left-border-blue workout-header-right-border-blue w-72 shrink-0 flex items-center gap-1 ${bottomBorder}`} style={{fontWeight: "normal"}}>
                        <div className="flex-1 min-w-0">
                            <Select
                                closeMenuOnSelect={true}
                                components={animatedComponents}
                                options={mvmtList ? mvmtList : []}
                                onChange={(mObj) => changeCategoryMovementCallback(category.category.seq, m.seq, mObj)}
                                value={_.find(mvmtList, mvmt => mvmt.value === m.movementId)}
                                styles={WORKOUT_SELECT_STYLE_SETTINGS}
                            />
                        </div>
                        <ButtonGroup>
                            <Button
                                color="info"
                                size="sm"
                                onClick={() => addMovementCallback(category.category.seq, m.seq)}
                            >
                                {mPlusIcon}
                            </Button>
                            <Button
                                color="danger"
                                size="sm"
                                onClick={() => removeMovementCallback(category.category.seq, m.seq)}
                            >
                                {mTrash}
                            </Button>
                        </ButtonGroup>
                    </div>

                    {/* Matrix difficulty cells: smaller, equal-width columns */}
                    {matrix && _.map(matrix.sortedPieces, (piece, pIdx) => {
                        const exerciseList = exGroups[m.movementId];
                        return (
                          <div key={`piece-${piece.phaseId}-${piece.movementId}-${piece.difficultyId}`}
                            className={`matrix-cell matrix-cell-l-margin workout-header-x-border flex-1 min-w-0 ${bottomBorder}`}>
                            <Select
                              closeMenuOnSelect
                              components={animatedComponents}
                              options={exerciseList ? exerciseList : []}
                              onChange={(eObj) => changeMatrixExerciseCallback(matrix, eObj, pIdx)}
                              value={_.find(exerciseList, ex => ex.value === piece.exerciseId)}
                              styles={SELECT_STYLE_SETTINGS}
                            />
                          </div>
                        )
                    })}
                </div>
            );
        })}
    </div>);
});

export default function ProgramMovementMatrix(props){
    const [filters, setFilters] = useState([]);
    const [filterDisplayText, setFilterDisplayText] = useState("");
    const [selectedOrg, setSelectedOrg] = useState(null);
    const [orgsList, setOrgsList] = useState([]);
    const [phaseList, setPhaseList] = useState([]);
    const [periodList, setPeriodList] = useState([]);
    const [workoutList, setWorkoutList] = useState([]);
    const [movementList, setMovementList] = useState([]);
    const [exerciseGroupedList, setExerciseGroupedList] = useState([]);
    const [program, setProgram] = useState(null);
    const [matrixList, setMatrixList] = useState(null);
    const [messages, setMessages] = useState([]);
    const [dataChanged, setDataChanged] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadedOnce, setLoadedOnce] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fetchNewMatrix, setFetchNewMatrix] = useState(false);
    // "Active Workouts" toggle. On (default) => the Phase/Period/Workout dropdowns
    // are re-fetched to list only items backed by an active workout chain.
    const [activeWorkoutsOnly, setActiveWorkoutsOnly] = useState(true);

    const userCtx = useContext(UserContext);
    const isSysAdmin = _.includes(userCtx.currentUser.roleTypeIdList, constants.ROLE_IDS.SYSADMIN);

    useEffect(() => {
        LoadOrgs();
    }, []);

    useEffect(()=>{
        if(!selectedOrg) return;
        GetPageLists();
    }, [selectedOrg, activeWorkoutsOnly]);

    useEffect(()=> {
        if(messages){
            setTimeout(() => {
              setMessages([]);
            }, 15000);
        }        
    }, [messages]);

    useEffect(()=>{
        if (!program || !loadedOnce || !fetchNewMatrix) 
          return;
        GetMatrixData();
    }, [fetchNewMatrix]);

    useEffect(() => {
        let filterDisplay = "";
        if (!filters || !filters.length || filters.length === 0) {
          // Bail out — a useEffect's return value is treated as its cleanup
          // function, so returning a string crashes on unmount/re-run.
          return;
        }
        filterDisplay = _.chain(filters)
          .map((x) => {
            return x.value !== null || x.values || x.startDate || x.endDate
              ? x.filteredDisplay
              : null;
          })
          .filter((x) => x !== null && x !== undefined)
          .value()
          .join(" | ");
        setFilterDisplayText(filterDisplay !== "" ? ` - ${filterDisplay}` : "");
      }, [filters]);

    function GetPageLists(){
        api.fetch(`WorkoutAdmin/GetProgramMovementMatrixLists/${selectedOrg.value}?activeWorkoutsOnly=${activeWorkoutsOnly}`)
        .then((response) => {
            if(response.data && response.data.success) {
                let vm = response.data.message;
                // Track the refreshed dropdown options so a flipped toggle can drop any
                // Phase/Period/Workout selection that no longer exists in the new lists.
                const refreshed = {};
                if(vm.phases) {
                    let phases = helpers.idNameToValueLabel(vm.phases);
                    setPhaseList(phases);
                    refreshed.PhaseId = phases;
                }
                if(vm.periods) {
                    let periods = helpers.idNameToValueLabel(vm.periods);
                    setPeriodList(periods);
                    refreshed.PeriodId = periods;
                }
                if(vm.workouts) {
                    let workouts = helpers.idNameToValueLabel(vm.workouts);
                    setWorkoutList(workouts);
                    refreshed.OrgWorkoutId = workouts;
                }
                reconcileDropdownFilters(refreshed);
                // Cascading mode: the lists endpoint only returns the (chain-filtered)
                // Phase list; Period/Workout arrive empty and are loaded on demand. If a
                // still-valid Phase is selected (e.g. after flipping the toggle), reload
                // its Periods so the cascade picks up where it left off.
                if(activeWorkoutsOnly) {
                    const currentPhase = filterHelpers.currentFilterValue(filters, "PhaseId");
                    const phaseId = currentPhase ? currentPhase.value : null;
                    if(phaseId && _.some(refreshed.PhaseId || [], (o) => o.value === phaseId)) {
                        GetCascadingPeriods(phaseId);
                    }
                }
                if(vm.movements) {
                    let movements = helpers.idNameToValueLabel(vm.movements);
                    movements = _.orderBy(movements, ['label'], ['asc']);
                    setMovementList(movements);
                }
                if(vm.exerciseGroups) {
                    let copy = Object.assign({}, vm.exerciseGroups);
                    for (const property in vm.exerciseGroups) {
                         let mappedProperty = helpers.idNameToValueLabel(vm.exerciseGroups[property]);
                         mappedProperty = _.orderBy(mappedProperty, ['label'], ['asc']);
                         copy[property] = mappedProperty;
                    }
                    setExerciseGroupedList(copy);
                }
            }
        })
        .catch(api.catchHandler);   
    }

    // Cascade step 2: periods that have an active workout chain under the chosen phase.
    function GetCascadingPeriods(phaseId){
        api.fetch(`WorkoutAdmin/GetFilteredPeriods/${selectedOrg.value}/${phaseId}`)
        .then((response) => {
            if(response.data && response.data.success) {
                setPeriodList(helpers.idNameToValueLabel(response.data.message || []));
            }
        })
        .catch(api.catchHandler);
    }

    // Cascade step 3: workouts with active OrgWorkoutCategories + OrgWorkoutCategoryMovements
    // under the chosen phase + period.
    function GetCascadingWorkouts(phaseId, periodId){
        api.fetch(`WorkoutAdmin/GetFilteredWorkouts/${selectedOrg.value}/${phaseId}/${periodId}`)
        .then((response) => {
            if(response.data && response.data.success) {
                setWorkoutList(helpers.idNameToValueLabel(response.data.message || []));
            }
        })
        .catch(api.catchHandler);
    }

    function LoadOrgs () {
        api
        .post(`OrgAdmin/GetOrgSimpleList`, {
        ActiveOnly: true,
        })
        .then((r) => {
            if (r.data && r.data.success) {
                let result = _.map(r.data.message, (x) => {
                    return {
                        label: x.name,
                        value: x.id,
                    };
                });
                if (!isSysAdmin) {
                    result = _.filter(
                        result,
                        (x) => x.value === userCtx.currentUser.orgId
                    );
                }
                setOrgsList(result);
                setSelectedOrg(_.find(result, x => x.value === userCtx.currentUser.orgId));
            }
        })
        .catch(api.catchHandler);
    };

    function IsValid(){
        let errors = [];
        if(!filterHelpers.currentFilterValue(filters, "PhaseId")){
            errors.push("Missing selected phase.");
        }
        if(!filterHelpers.currentFilterValue(filters, "PeriodId")){
            errors.push("Missing selected period.");
        }
        if(!filterHelpers.currentFilterValue(filters, "OrgWorkoutId")){
            errors.push("Missing selected workout.");
        }
        if(!selectedOrg || !selectedOrg.value){
            errors.push("Missing selected Organization.");
        }
        if(errors.length){
            let errorObjs = _.map(errors, msg => {
                return {
                    flavor: "danger",
                    text: msg
                };
            });
            setMessages(errorObjs);
            return false;
        }
        return true;
    }

    function CreateProgramCriteriaPayload(){
        return {
            ActiveOnly: true,
            OrgId: selectedOrg.value,
            PhaseId: filterHelpers.currentFilterValue(filters, "PhaseId").value,
            PeriodId: filterHelpers.currentFilterValue(filters, "PeriodId").value,
            OrgWorkoutId: filterHelpers.currentFilterValue(filters,"OrgWorkoutId").value
        };
    }

    function GetProgramMovementMatrixData(){
      if (!IsValid()) return;
      clearProgramAndMatrix();
      let payload = CreateProgramCriteriaPayload();
      api.post(`WorkoutAdmin/GetProgramAndMovementMatrix`, payload).then((r) => {
        if (r.data && r.data.success) {
          setMatrixList(r.data.message.movementMatrixList);
          setProgram(r.data.message.programBuilderVM);
          setLoadedOnce(true);
        }
        else
        {
          setMessages([{ flavor: "danger", text: r.data.message }]);
        }
      }).catch(api.catchHandler);
    }

    function GetMatrixData() {
        let movementIdList = [];
        // console.log('p', program)
        _.forEach(program.categories, (cat) => {
          let movementIds = _.map(cat.movements, move => move.movementId);
          movementIdList = movementIdList.concat(movementIds);
        })
        setLoading(true)
        const payload = {
          OrgId: selectedOrg.value,
          PhaseId: filterHelpers.currentFilterValue(filters, "PhaseId").value,
          ActiveOnly: true,
          PeriodId: program.orgWorkout.periodId,
          OrgWorkoutId: program.orgWorkout.orgWorkoutId,
          OverrideMovementIds: _.uniq(movementIdList)
        };
        api.post("WorkoutAdmin/GetMatrixList2", payload).then((response) => {
          if(response.data && response.data.success){
            setMatrixList(response.data.message);
          }
        })
        .catch(api.catchHandler)
        .finally(() => {
            setLoading(false);
            setFetchNewMatrix(false);
        });
    }

    function CreateSavePayload() {
      return {
        ProgramBuilderVM: program,
        MovementMatrixList: matrixList
      };
    }

    function SaveProgramMovementMatrixData(){
        if (isSaving) return;
        setLoading(true);
        setIsSaving(true);
        api.post("WorkoutAdmin/SaveProgramAndMovementMatrixData", CreateSavePayload()).then((response) => {
            if (response.data && response.data.success) {
                setMessages([{
                    flavor: "success",
                    text: "Changes were saved successfully"
                }]);
                //clearFilterSet();
                GetProgramMovementMatrixData();
            }
        }).catch(err => {
            setMessages([{
                flavor: "danger",
                text: "Error saving changes"
            }]);
            api.catchHandler(err);
        })
        .finally(() => {
            setIsSaving(false);
            setLoading(false);
        });
    }

    function clearFilterSet() {
        setFilterDisplayText("");
        setFilters([]);
        clearProgramAndMatrix();
    }

    function resetFilters(){
        setFilters([
            { filterName: "ActiveOnly", value: true, filteredDisplay: "Active Only" },
        ]);
    }

    function onFilterChange(changedFilter) {
        const filterChanges = filterHelpers.getFilters(filters, changedFilter);
        setFilters((prev) => {
            if (prev) {
            prev = filterChanges;
            }
            return prev;
        });
    }

    // Drop any Phase/Period/Workout filter whose selected value is no longer among
    // the refreshed dropdown options (e.g. after the Active Workouts toggle flips),
    // and clear the loaded program so no stale selection lingers. `refreshedOptions`
    // maps filterName -> [{value, label}]; lists not present are left untouched.
    function reconcileDropdownFilters(refreshedOptions){
        let changed = false;
        const next = filters.filter((f) => {
            const opts = refreshedOptions[f.filterName];
            if (!opts) return true;
            const stillValid = f.value && _.some(opts, (o) => o.value === f.value.value);
            if (!stillValid) changed = true;
            return stillValid;
        });
        if (changed) {
            setFilters(next);
            clearProgramAndMatrix();
        }
    }

    // Phase is the top of the cascade. Changing it invalidates any Period/Workout
    // selection and reloads the Period list for the new phase (cascading mode only).
    function onPhaseChange(e){
        let next = filterHelpers.getFilters(filters, e);
        if (activeWorkoutsOnly) {
            next = next.filter((f) => f.filterName !== "PeriodId" && f.filterName !== "OrgWorkoutId");
        }
        setFilters(next);
        clearProgramAndMatrix();
        if (!activeWorkoutsOnly) return;
        setWorkoutList([]);
        const phaseId = e.value ? e.value.value : null;
        if (phaseId) {
            GetCascadingPeriods(phaseId);
        } else {
            setPeriodList([]);
        }
    }

    // Period is the second cascade step. Changing it invalidates any Workout selection
    // and reloads the Workout list for the current phase + period (cascading mode only).
    function onPeriodChange(e){
        let next = filterHelpers.getFilters(filters, e);
        if (activeWorkoutsOnly) {
            next = next.filter((f) => f.filterName !== "OrgWorkoutId");
        }
        setFilters(next);
        clearProgramAndMatrix();
        if (!activeWorkoutsOnly) return;
        const currentPhase = filterHelpers.currentFilterValue(filters, "PhaseId");
        const phaseId = currentPhase ? currentPhase.value : null;
        const periodId = e.value ? e.value.value : null;
        if (phaseId && periodId) {
            GetCascadingWorkouts(phaseId, periodId);
        } else {
            setWorkoutList([]);
        }
    }

    function clearProgramAndMatrix(){
        setProgram(null);
        setMatrixList(null);
        setDataChanged(false);
    }

    function clearFilterOptions(){
      setPhaseList([]);
      setPeriodList([]);
      setWorkoutList([]);
    }

    // All of these use the functional updater form of setState and touch nothing
    // that changes between renders, so they are stable (useCallback []). Combined
    // with immutable updates (new refs only for the changed category/movement),
    // the memoized ProgramSectionAndMatrix rows only re-render when their own data
    // actually changes.
    const onCategoryMovementChange = useCallback((categorySequence, movementSequence, movementSelect) => {
      setProgram((prev) => {
        if (!prev) return prev;
        const cIdx = categorySequence - 1;
        const mIdx = movementSequence - 1;
        const categories = prev.categories.map((c, i) => {
          if (i !== cIdx) return c;
          const movements = c.movements.map((mv, j) =>
            j === mIdx
              ? { ...mv, movementId: movementSelect.value, name: movementSelect.label }
              : mv
          );
          return { ...c, movements };
        });
        return { ...prev, categories };
      });
      // Keep the current matrix mounted while the refreshed data loads (see the
      // `loading` overlay in the render) instead of blanking the whole grid.
      setFetchNewMatrix(true);
      setDataChanged(true);
    }, []);

    const onChangeMatrixExercise = useCallback((matrix, exerciseSelect, pieceIndex) => {
      setMatrixList((prev) => {
        if (!prev) return prev;
        const matrixIndex = _.findIndex(prev, (mtx) =>
          mtx.movementId === matrix.movementId
          && mtx.phaseId === matrix.phaseId
          && mtx.orgWorkoutCategoryId === matrix.orgWorkoutCategoryId);
        if (matrixIndex < 0) return prev;
        const target = prev[matrixIndex];
        const updated = {
          ...target,
          sortedPieces: target.sortedPieces.map((p, i) =>
            i === pieceIndex ? { ...p, exerciseId: exerciseSelect.value } : p
          ),
        };
        const copy = [...prev];
        copy[matrixIndex] = updated;
        return copy;
      });
      setDataChanged(true);
    }, []);

    const onCategoryHeaderChange = useCallback((categorySequence, typeOfHeader, value) => {
      const nextValue = !value || !value.length ? null : value;
      setProgram((prev) => {
        if (!prev) return prev;
        const cIdx = categorySequence - 1;
        const categories = prev.categories.map((c, i) =>
          i === cIdx
            ? { ...c, category: { ...c.category, [typeOfHeader]: nextValue } }
            : c
        );
        return { ...prev, categories };
      });
      setDataChanged(true);
    }, []);

    const addCategory = useCallback((categorySequence) => {
      setProgram((prev) => {
        if (!prev) return prev;
        const insertIndex = categorySequence; // insert directly after the source category
        const categoryCopy = JSON.parse(JSON.stringify(prev.categories[categorySequence - 1]));
        categoryCopy.category.id = 0;
        const categories = [...prev.categories];
        categories.splice(insertIndex, 0, categoryCopy);
        // Re-sequence immutably: every category gets a fresh ref carrying its new seq.
        const resequenced = categories.map((c, i) => ({
          ...c,
          category: { ...c.category, seq: i + 1 },
        }));
        const newSeq = resequenced[insertIndex].category.seq;
        resequenced[insertIndex] = {
          ...resequenced[insertIndex],
          category: {
            ...resequenced[insertIndex].category,
            header: `Header ${newSeq}`,
            subHeader: `Sub Header ${newSeq}`,
          },
        };
        return { ...prev, categories: resequenced };
      });
      setFetchNewMatrix(true);
      setDataChanged(true);
    }, []);

    const removeCategory = useCallback((categorySequence) => {
      setProgram((prev) => {
        if (!prev) return prev;
        const categories = prev.categories
          .filter((_c, i) => i !== categorySequence - 1)
          .map((c, i) => ({ ...c, category: { ...c.category, seq: i + 1 } }));
        return { ...prev, categories };
      });
      setFetchNewMatrix(true);
      setDataChanged(true);
    }, []);

    const addMovementToCategory = useCallback((categorySequence, movementSequence) => {
      setProgram((prev) => {
        if (!prev) return prev;
        const cIdx = categorySequence - 1;
        const mIdx = movementSequence - 1;
        const categories = prev.categories.map((c, i) => {
          if (i !== cIdx) return c;
          const movementCopy = JSON.parse(JSON.stringify(c.movements[mIdx]));
          const movements = [...c.movements];
          movements.splice(mIdx + 1, 0, movementCopy);
          return { ...c, movements: movements.map((mv, j) => ({ ...mv, seq: j + 1 })) };
        });
        return { ...prev, categories };
      });
      // Keep the current matrix mounted while the refreshed data loads (see the
      // `loading` overlay in the render) instead of blanking the whole grid.
      setFetchNewMatrix(true);
      setDataChanged(true);
    }, []);

    const removeMovementfromCategory = useCallback((categorySequence, movementSequence) => {
      setProgram((prev) => {
        if (!prev) return prev;
        const cIdx = categorySequence - 1;
        const mIdx = movementSequence - 1;
        const categories = prev.categories.map((c, i) => {
          if (i !== cIdx) return c;
          const movements = c.movements
            .filter((_mv, j) => j !== mIdx)
            .map((mv, j) => ({ ...mv, seq: j + 1 }));
          return { ...c, movements };
        });
        return { ...prev, categories };
      });
      // Keep the current matrix mounted while the refreshed data loads (see the
      // `loading` overlay in the render) instead of blanking the whole grid.
      setFetchNewMatrix(true);
      setDataChanged(true);
    }, []);

    return (
        <Fragment>
            <DesktopContainer screenName="PROGRAMMING" textColor="blue-txt">
                {messages && messages.length
                    ? (<Row>
                        <Col xs="4">
                            {_.map(messages, (msg, index) => {
                                return (
                                    <Alert2
                                    key={`A-${index}`}
                                    className={msg.flavor}
                                    style={{ marginTop: "15px", borderRadius: "15px" }}
                                    fade
                                    >
                                        {msg.text}
                                    </Alert2>
                                )
                            })}
                        </Col>
                    </Row>)
                    : null
                }
                <Row>
                    <Col sm="3">
                        <Select
                            isMulti={false}
                            closeMenuOnSelect={true}
                            components={animatedComponents}
                            options={orgsList ? orgsList : []}
                            onChange={(org) => {
                                setSelectedOrg(org);
                                resetFilters();
                                clearFilterOptions();
                                clearProgramAndMatrix();
                            }}
                            isClearable={true}
                            value={selectedOrg}
                            onBlurResetsInput={false}
                            onSelectResetsInput={false}
                            onCloseResetsInput={false}
                            classNamePrefix="react-select"
                        />
                    </Col>
                </Row>
                {selectedOrg
                    ?   (<>
                            <Row>
                                <Col xs="12">
                                <span title="Toggle filter display" className="filter m-1">
                                    <FontAwesomeIcon icon="filter" /> Filters
                                </span>
                                <span
                                    className="filter-display m-1"
                                    title={filterDisplayText}
                                >
                                    {filterDisplayText}
                                </span>
                                {filters && filters.length !== 0 && (
                                    <Button
                                    className="filter-display  m-1 ml-4"
                                    size="sm"
                                    onClick={clearFilterSet}
                                    >
                                    <FontAwesomeIcon
                                        icon="backspace"
                                        // className="mr-2 ml-0 mt-0 mb-0"
                                    />{" "}
                                    Clear
                                    </Button>
                                )}
                                </Col>
                            </Row>
                            <Row>
                                <Col sm="3">
                                    <FilterSwitch
                                        filterName="ActiveWorkoutsOnly"
                                        displayName="Active Workouts"
                                        value={activeWorkoutsOnly}
                                        onChangeCallback={(e) => setActiveWorkoutsOnly(!!e.value)}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Row>
                                        <Col sm="3">
                                            <FilterSelect
                                                filterName="PhaseId"
                                                displayName="Phase"
                                                value={filterHelpers.currentFilterValue(
                                                filters,
                                                "PhaseId"
                                                )}
                                                onChangeCallback={(e) => onPhaseChange(e)}
                                                options={phaseList ? phaseList : []}
                                            />
                                        </Col>
                                        <Col sm="3">
                                            <FilterSelect
                                                filterName="PeriodId"
                                                displayName="Period"
                                                value={filterHelpers.currentFilterValue(
                                                filters,
                                                "PeriodId"
                                                )}
                                                onChangeCallback={(e) => onPeriodChange(e)}
                                                options={periodList ? periodList : []}
                                            />
                                        </Col>
                                        <Col sm="3">
                                            <FilterSelect
                                                filterName="OrgWorkoutId"
                                                displayName="Workout"
                                                value={filterHelpers.currentFilterValue(
                                                filters,
                                                "OrgWorkoutId"
                                                )}
                                                onChangeCallback={(e) => {
                                                    onFilterChange(e);
                                                    clearProgramAndMatrix();
                                                }}
                                                options={workoutList ? workoutList : []}
                                            />
                                        </Col>
                                        <Col sm="3" style={{verticalAlign: "bottom"}}>
                                            <Button
                                                className={"program-matrix-get"}
                                                onClick={GetProgramMovementMatrixData}
                                                disabled={messages && messages.length > 0}
                                            >
                                                Get Workout
                                            </Button>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </>)
                    : null}
                {program && matrixList && matrixList.length
                    ?   (<div className="relative">
                            {/* Non-blocking refresh indicator: dim + lock the grid while the
                                matrix reloads instead of unmounting the whole screen. */}
                            {loading ? (
                                <div className="absolute inset-0 z-10 flex items-start justify-center pt-4 bg-white/60 pointer-events-none">
                                    <span className="badge badge-info">Updating…</span>
                                </div>
                            ) : null}
                            <div className={loading ? "opacity-50 pointer-events-none" : ""}>
                            <Row className="mx-1">
                                {/* <Col className="program-matrix-header program-cell">&nbsp;</Col>
                                <Col className="program-matrix-header matrix-cell matrix-header">&nbsp;</Col>
                                <Col className="program-matrix-header matrix-cell matrix-header">&nbsp;</Col>
                                <Col className="program-matrix-header matrix-cell matrix-header">&nbsp;</Col> */}
                                <Col className="program-matrix-header movement-matrix-borders matrix-header program-matrix-top-border blue-bg-white-txt workout-header-bottom-border" >Movement Matrix</Col>
                                {/* <Col className="program-matrix-header matrix-cell matrix-header">&nbsp;</Col>
                                <Col className="program-matrix-header matrix-cell matrix-header">&nbsp;</Col>
                                <Col className="program-matrix-header matrix-cell matrix-header">&nbsp;</Col> */}
                            </Row>
                            {/* Difficulty header row — mirrors the ProgramSectionAndMatrix column model:
                                a fixed-width first column (Workout, aligned over the select+buttons)
                                followed by equal-width difficulty columns. */}
                            <div className="flex mx-1">
                                <div className="program-matrix-header white-bg workout-header-left-border-blue workout-header-right-border-blue workout-header-bottom-border w-72 shrink-0">Workout</div>
                                {["-3", "-2", "-1", "Base", "1", "2", "3"].map((label) => (
                                    <div key={`difficulty-${label}`} className="program-matrix-header white-bg matrix-header workout-header-x-border workout-header-bottom-border matrix-cell-l-margin flex-1 min-w-0">{label}</div>
                                ))}
                            </div>
                            {_.map(program.categories, (pc, index) => {
                                let lastCategory = index === program.categories.length - 1;
                                return (<ProgramSectionAndMatrix 
                                    key={`program-cat-${index}`}
                                    category={pc}
                                    mtrxList={matrixList}
                                    mvmtList={movementList}
                                    exGroups={exerciseGroupedList}
                                    changeCategoryMovementCallback={onCategoryMovementChange}
                                    changeMatrixExerciseCallback={onChangeMatrixExercise}
                                    changeHeaderCallback={onCategoryHeaderChange}
                                    addCategoryCallback={addCategory}
                                    removeCategoryCallback={removeCategory}
                                    addMovementCallback={addMovementToCategory}
                                    removeMovementCallback={removeMovementfromCategory}
                                    lastCategory={lastCategory}
                                />);
                            })}
                            <Row className="m-1">
                                <Col className="p-0">
                                    <Button
                                      className="program-matrix-save"
                                      onClick={SaveProgramMovementMatrixData}
                                      disabled={!dataChanged}
                                    >
                                      Save
                                    </Button>
                                </Col>
                            </Row>
                            </div>
                        </div>)
                    : null}
            </DesktopContainer>
        </Fragment>
    )
}